import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, useConfirm, EmptyState, LoadingState, RelatedLinks } from '../components/ui';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { FlowStepBar, useLinkedInFlow, LinkedInConnectionCard } from '../linkedin';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { Vorschaubild } from '../components/media/Vorschaubild';
import { useVorschauJeIdee } from '../components/media/useVorschauJeIdee';
import {
  publicationCalendarApi,
  PublicationCalendarEntry,
  LinkedInMedienKandidat,
  linkedInApi,
  SocialPublication,
} from '../services/api';
import { SocialVeroeffentlichungen } from '../social/SocialVeroeffentlichungen';
import { PLATTFORM_NAME } from '../social/plattformen';
import { useVeroeffentlichungsprofile, profilOption } from '../social/useVeroeffentlichungsprofile';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  scheduled: 'Geplant',
  approved: 'Freigegeben',
  published: 'Veroeffentlicht',
  publishing: 'Wird veröffentlicht',
  failed: 'Fehlgeschlagen',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--color-text-secondary)',
  scheduled: 'var(--color-primary)',
  approved: 'var(--color-success)',
  published: 'var(--sec-success)',
  publishing: 'var(--color-primary)',
  failed: 'var(--color-danger)',
};

/** Status, die einer Freigabe gleichkommen (Backend: FREIGABE_STATUS der Kalender-Routen). */
const FREIGABE_STATUS = ['approved', 'published'];

/** Aus diesen Status heraus darf auf LinkedIn veroeffentlicht werden (Backend: publishToLinkedIn). */
const VEROEFFENTLICHBAR = ['approved', 'scheduled'];

const MEDIEN_ART: Record<string, string> = { image: 'Bild', video: 'Video', pdf: 'PDF-Karussell' };

/** Solange ein Eintrag auf `publishing` steht, wird die Liste so oft still neu geladen. */
const NACHLADEN_MS = 10000;

const megabyte = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;

/**
 * Veroeffentlichungskalender. Rechte (Paket 2): Freigeben und
 * Veroeffentlichen brauchen linkedin.freigeben; Planen, Bearbeiten und
 * Loeschen eines Eintrags gehen mit linkedin.bearbeiten.
 * Seit der Welle-2-Nacharbeit prueft auch PUT /publication-calendar/:id:
 * status 'approved' oder 'published' braucht dort linkedin.freigeben.
 * Freigabe und Veroeffentlichung laufen deshalb nur ueber die eigenen
 * Routen (approve, publish) und nur mit dem Recht; das Bearbeiten-Formular
 * schickt nie einen Status, nur Datum, Uhrzeit und Notizen.
 */
const PublicationCalendarPage: React.FC = () => {
  const { confirm } = useConfirm();
  const darfFreigeben = useBerechtigung().darf('linkedin.freigeben');
  const { refresh } = useLinkedInFlow();
  const [entries, setEntries] = useState<PublicationCalendarEntry[]>([]);
  // Instagram-, X- und Facebook-Eintraege (eigene Liste, gemeinsamer Kalender).
  const [socialEintraege, setSocialEintraege] = useState<SocialPublication[]>([]);
  const socialGeladen = useCallback((liste: SocialPublication[]) => setSocialEintraege(liste), []);
  // Hauptmedium der Beitragsidee je Eintrag fuer das Vorschaubild.
  const vorschauJeIdee = useVorschauJeIdee([
    ...entries.map((e) => e.linkedInPost?.postIdeaId),
    ...socialEintraege.map((e) => e.socialPost?.postIdeaId),
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [selectedEntry, setSelectedEntry] = useState<PublicationCalendarEntry | null>(null);
  const [searchParams] = useSearchParams();
  const presetEntryId = searchParams.get('entry');
  const [presetDone, setPresetDone] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PublicationCalendarEntry | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [linkedInConnected, setLinkedInConnected] = useState(false);
  const [linkedInDryRun, setLinkedInDryRun] = useState(false);
  const [erfolg, setErfolg] = useState<{ text: string; link?: string } | null>(null);
  const [laeuftFuer, setLaeuftFuer] = useState<string | null>(null);
  const [medienAuswahl, setMedienAuswahl] = useState<{
    entry: PublicationCalendarEntry;
    kandidaten: LinkedInMedienKandidat[];
    gewaehlt: string;
  } | null>(null);
  const [markieren, setMarkieren] = useState<{ entry: PublicationCalendarEntry; link: string } | null>(null);
  // Veroeffentlichungsprofile: LinkedIn-Profile der Personen im Team ("Mein Profil verbinden").
  const profile = useVeroeffentlichungsprofile();
  const linkedInProfile = profile.fuer('linkedin');

  useEffect(() => {
    loadEntries();
    checkLinkedInStatus();
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Video/PDF-Uploads laufen im Hintergrund weiter (Status `publishing`);
  // bis sie fertig sind, laedt die Liste still nach.
  const laeuftNoch = entries.some((e) => e.status === 'publishing');
  useEffect(() => {
    if (!laeuftNoch) return undefined;
    const uhr = window.setInterval(() => { loadEntries(true); }, NACHLADEN_MS);
    return () => window.clearInterval(uhr);
  }, [laeuftNoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sprung-Chip aus dem Wochenplan: ?entry=<id> oeffnet den Eintrag einmal.
  useEffect(() => {
    if (presetDone || !presetEntryId || loading) return;
    const entry = entries.find((e) => e.id === presetEntryId);
    if (entry) setSelectedEntry(entry);
    setPresetDone(true);
  }, [presetDone, presetEntryId, loading, entries]);

  const loadEntries = async (still = false) => {
    try {
      if (!still) setLoading(true);
      const data = await publicationCalendarApi.getAll(
        filterStatus ? { status: filterStatus } : undefined
      );
      setEntries(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden des Kalenders');
    } finally {
      setLoading(false);
    }
  };

  const checkLinkedInStatus = async () => {
    try {
      const status = await linkedInApi.getStatus();
      setLinkedInConnected(status.connections.some((c) => c.isActive));
      setLinkedInDryRun(!!status.dryRun);
    } catch {
      setLinkedInConnected(false);
    }
  };

  const handleStatusChange = async (entryId: string, newStatus: string) => {
    // Ohne linkedin.freigeben gibt es die Knoepfe nicht; der Riegel hier
    // verhindert, dass ein spaeterer Aufrufer trotzdem freigibt.
    if (FREIGABE_STATUS.includes(newStatus) && !darfFreigeben) return;
    try {
      if (newStatus === 'approved') {
        await publicationCalendarApi.approve(entryId);
      } else if (newStatus === 'scheduled') {
        await publicationCalendarApi.schedule(entryId);
      } else {
        await publicationCalendarApi.update(entryId, { status: newStatus });
      }
      await loadEntries();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Status');
    }
  };

  /**
   * Postet ueber das eigene LinkedIn-Konto (Backend: publish-linkedin).
   * Haengen mehrere Medien an der Idee, fragt ein Dialog nach; im Testmodus
   * meldet das Backend nur, was gepostet wuerde.
   */
  const handlePublishToLinkedIn = async (entry: PublicationCalendarEntry, mediaAssetId?: string) => {
    if (!darfFreigeben) return;
    // Gibt es Team-Profile, muss das Profil gewaehlt sein: nie versehentlich ueber das eigene posten.
    if (!entry.linkedInConnectionId && linkedInProfile.length > 0) {
      setError('Bitte zuerst das LinkedIn-Profil wählen, über das der Beitrag erscheinen soll (Auswahl „Profil“ am Eintrag).');
      return;
    }
    if (!entry.linkedInConnectionId && !linkedInConnected && !linkedInDryRun) {
      setError('Bitte zuerst das LinkedIn-Konto verbinden (Karte oben auf dieser Seite).');
      return;
    }
    setError('');
    setErfolg(null);
    setLaeuftFuer(entry.id);
    try {
      const r = await publicationCalendarApi.publishToLinkedIn(entry.id, mediaAssetId);
      setMedienAuswahl(null);
      if (r.simulated) {
        const womit = r.medium
          ? `mit „${r.medium.title}“ (${MEDIEN_ART[r.medium.kind] || r.medium.kind})`
          : 'als reiner Textbeitrag';
        const ohneKonto = r.verbunden === false ? ' Hinweis: dein LinkedIn-Konto ist noch nicht verbunden.' : '';
        setErfolg({ text: `Testmodus: nichts gepostet. Der Beitrag würde ${womit} veröffentlicht (${r.zeichen ?? 0} Zeichen).${ohneKonto}` });
      } else if (r.laeuft) {
        setErfolg({ text: 'Wird auf LinkedIn hochgeladen. LinkedIn verarbeitet das Medium noch – der Status aktualisiert sich von selbst.' });
      } else {
        setErfolg({ text: 'Auf LinkedIn veröffentlicht.', link: r.linkedInPostUrl || r.entry?.linkedInPostUrl || undefined });
      }
      await loadEntries(true);
      refresh();
    } catch (err: any) {
      const daten = err.response?.data;
      if (err.response?.status === 400 && Array.isArray(daten?.kandidaten) && daten.kandidaten.length > 0) {
        setMedienAuswahl({ entry, kandidaten: daten.kandidaten, gewaehlt: daten.kandidaten[0].id });
      } else {
        setError(daten?.error || 'Fehler beim Veröffentlichen auf LinkedIn');
        await loadEntries(true);
      }
    } finally {
      setLaeuftFuer(null);
    }
  };

  /** LinkedIn-Profil am Eintrag waehlen (nur mit linkedin.freigeben; leer = keins). */
  const handleProfilWaehlen = async (entry: PublicationCalendarEntry, linkedInConnectionId: string) => {
    try {
      setError('');
      await publicationCalendarApi.update(entry.id, { linkedInConnectionId: linkedInConnectionId || null });
      await loadEntries(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Das Profil konnte nicht gespeichert werden.');
    }
  };

  /** Name des gewaehlten Profils (oder Hinweis, falls nicht mehr verbunden). */
  const profilName = (entry: PublicationCalendarEntry): string | null => {
    if (!entry.linkedInConnectionId) return null;
    return profile.finden(entry.linkedInConnectionId)?.anzeigeName ?? 'Profil nicht mehr verbunden';
  };

  /** "Als veröffentlicht markieren": nach manuellem Posten, Link optional. */
  const handleMarkPublished = async () => {
    if (!markieren || !darfFreigeben) return;
    try {
      const link = markieren.link.trim();
      await publicationCalendarApi.markPublished(markieren.entry.id, link || undefined);
      setMarkieren(null);
      await loadEntries();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Markieren als veröffentlicht');
    }
  };

  const handleEditEntry = (entry: PublicationCalendarEntry) => {
    setEditingEntry(entry);
    const date = new Date(entry.scheduledDate);
    setEditDate(date.toISOString().split('T')[0]);
    setEditTime(entry.scheduledTime || '10:00');
    setEditNotes(entry.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    try {
      // Bewusst ohne status: Freigeben/Veroeffentlichen ist kein Formularfeld.
      await publicationCalendarApi.update(editingEntry.id, {
        scheduledDate: `${editDate}T${editTime}:00`,
        scheduledTime: editTime,
        notes: editNotes,
      });
      setEditingEntry(null);
      await loadEntries();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    const ok = await confirm({ title: 'Kalendereintrag löschen?', message: 'Der Beitrag selbst bleibt erhalten.', confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;

    try {
      await publicationCalendarApi.delete(entryId);
      setSelectedEntry(null);
      await loadEntries();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (entry: PublicationCalendarEntry) => {
    if (entry.scheduledTime) return entry.scheduledTime;
    const date = new Date(entry.scheduledDate);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const getPostPreview = (entry: PublicationCalendarEntry) => {
    if (!entry.linkedInPost) return 'Kein Beitrag';
    const text = entry.linkedInPost.fullPost || entry.linkedInPost.hook;
    return text.substring(0, 120).replace(/\\n/g, ' ') + (text.length > 120 ? '...' : '');
  };

  // Calendar view helpers
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1; // Monday = 0
  };

  const getEntriesForDate = (day: number) => {
    const targetDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return entries.filter((entry) => {
      const entryDate = new Date(entry.scheduledDate);
      return (
        entryDate.getFullYear() === targetDate.getFullYear() &&
        entryDate.getMonth() === targetDate.getMonth() &&
        entryDate.getDate() === targetDate.getDate()
      );
    });
  };

  const socialFuerTag = (day: number) =>
    socialEintraege.filter((e) => {
      const d = new Date(e.scheduledDate);
      return d.getFullYear() === currentMonth.getFullYear() && d.getMonth() === currentMonth.getMonth() && d.getDate() === day;
    });

  const navigateMonth = (direction: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const monthName = currentMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  // Stats
  const stats = {
    total: entries.length,
    draft: entries.filter((e) => e.status === 'draft').length,
    scheduled: entries.filter((e) => e.status === 'scheduled').length,
    approved: entries.filter((e) => e.status === 'approved').length,
    published: entries.filter((e) => e.status === 'published').length,
  };

  if (loading) {
    return <div className="page-container"><FlowStepBar stepId="calendar" /><LoadingState /></div>;
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="calendar" />
      <PageHeader
        eyebrow="LinkedIn-Studio · Planen & Veröffentlichen"
        title="Terminieren"
        hilfe="kalender"
        subtitle="Legt Datum und Uhrzeit fest. Veröffentlicht wird erst nach Freigabe & Versand."
        actions={(
          <>
            <button
              onClick={() => setViewMode('list')}
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            >
              Liste
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            >
              Kalender
            </button>
          </>
        )}
      />

      {erfolg && (
        <div className="alert alert-success" role="status">
          {erfolg.text}
          {erfolg.link && (
            <>
              {' '}
              <a href={erfolg.link} target="_blank" rel="noopener noreferrer">
                Beitrag auf LinkedIn ansehen
              </a>
            </>
          )}
          <button onClick={() => setErfolg(null)} aria-label="Meldung schließen" style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
            x
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
            x
          </button>
        </div>
      )}

      {/* Stats Bar */}
      <div className="calendar-stats">
        <div className="calendar-stat-item">
          <span className="calendar-stat-number">{stats.total}</span>
          <span className="calendar-stat-label">Gesamt</span>
        </div>
        <div className="calendar-stat-item">
          <span className="calendar-stat-number" style={{ color: STATUS_COLORS.draft }}>{stats.draft}</span>
          <span className="calendar-stat-label">Entwuerfe</span>
        </div>
        <div className="calendar-stat-item">
          <span className="calendar-stat-number" style={{ color: STATUS_COLORS.scheduled }}>{stats.scheduled}</span>
          <span className="calendar-stat-label">Geplant</span>
        </div>
        <div className="calendar-stat-item">
          <span className="calendar-stat-number" style={{ color: STATUS_COLORS.approved }}>{stats.approved}</span>
          <span className="calendar-stat-label">Freigegeben</span>
        </div>
        <div className="calendar-stat-item">
          <span className="calendar-stat-number" style={{ color: STATUS_COLORS.published }}>{stats.published}</span>
          <span className="calendar-stat-label">Veroeffentlicht</span>
        </div>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: '16px' }}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="form-select"
          style={{ width: 'auto', minWidth: '180px' }}
        >
          <option value="">Alle Status</option>
          <option value="draft">Entwurf</option>
          <option value="scheduled">Geplant</option>
          <option value="approved">Freigegeben</option>
          <option value="published">Veroeffentlicht</option>
          <option value="publishing">Wird veröffentlicht</option>
          <option value="failed">Fehlgeschlagen</option>
        </select>
      </div>

      <div style={{ marginBottom: 'var(--space-5)' }}>
        <LinkedInConnectionCard variant="compact" onChanged={() => { checkLinkedInStatus(); refresh(); }} />
      </div>
      {darfFreigeben && linkedInDryRun && (
        <div className="info-panel info-panel-warning" role="status" style={{ marginBottom: 'var(--space-4)' }}>
          <strong>Testmodus: es wird nichts gepostet.</strong> „Auf LinkedIn veröffentlichen“ prüft Text, Medium
          und Größe, ruft LinkedIn aber nicht auf und ändert den Status nicht (LINKEDIN_DRY_RUN).
        </div>
      )}
      {darfFreigeben && linkedInProfile.length > 0 && (
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
          Gepostet wird über das am Eintrag gewählte Profil. Weitere Profile verbinden die Personen im Team selbst unter „Mein Profil verbinden“.
        </p>
      )}
      {darfFreigeben && linkedInProfile.length === 0 && !linkedInConnected && (
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
          Gepostet wird über dein eigenes LinkedIn-Profil: zum Veröffentlichen zuerst oben dein LinkedIn-Konto verbinden.
        </p>
      )}
      {!darfFreigeben && (
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
          Freigeben und Veröffentlichen ist der Rolle Marketing, Vertrieb oder Inhaber:in vorbehalten.
        </p>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div>
          {entries.length === 0 ? (
            <EmptyState
              icon="clock"
              title="Noch keine Einträge im Kalender"
              description="Erstelle im Generator einen Beitrag, wähle die beste Version und plane sie hier ein."
              action={{ label: 'Zum Beitrags-Generator', to: '/linkedin-post-generator' }}
            />
          ) : (
            <div className="calendar-list">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="calendar-list-item"
                  onClick={() => setSelectedEntry(entry)}
                >
                  <div className="calendar-list-date">
                    <div className="calendar-list-day">{new Date(entry.scheduledDate).getDate()}</div>
                    <div className="calendar-list-month">
                      {new Date(entry.scheduledDate).toLocaleDateString('de-DE', { month: 'short' })}
                    </div>
                    <div className="calendar-list-time">{formatTime(entry)}</div>
                  </div>
                  {entry.linkedInPost?.postIdeaId && vorschauJeIdee[entry.linkedInPost.postIdeaId] && (
                    <Vorschaubild
                      assetId={vorschauJeIdee[entry.linkedInPost.postIdeaId]?.assetId}
                      kind={vorschauJeIdee[entry.linkedInPost.postIdeaId]?.kind}
                      vorschau={vorschauJeIdee[entry.linkedInPost.postIdeaId]?.vorschau}
                      groesse="m"
                      alt={`Vorschau zum Beitrag am ${new Date(entry.scheduledDate).toLocaleDateString('de-DE')}`}
                    />
                  )}
                  <div className="calendar-list-content">
                    <div className="calendar-list-preview">{getPostPreview(entry)}</div>
                    {entry.linkedInPost?.versionLabel && (
                      <span className="text-xs text-secondary">Version: {entry.linkedInPost.versionLabel}</span>
                    )}
                    {entry.notes && <span className="text-xs text-secondary"> | {entry.notes}</span>}
                    {darfFreigeben && linkedInProfile.length > 0 && VEROEFFENTLICHBAR.concat('draft').includes(entry.status) ? (
                      <div className="text-xs text-secondary">
                        <span aria-hidden="true">Profil: </span>
                        {/* Klick auf die Auswahl oeffnet nicht den Eintrag (die Zeile ist klickbar). */}
                        <select
                          value={entry.linkedInConnectionId || ''}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleProfilWaehlen(entry, e.target.value)}
                          aria-label={`LinkedIn-Profil für den Eintrag am ${new Date(entry.scheduledDate).toLocaleDateString('de-DE')}`}
                        >
                          <option value="">– Profil wählen –</option>
                          {linkedInProfile.map((p) => (
                            <option key={p.id} value={p.id}>{profilOption(p)}</option>
                          ))}
                          {entry.linkedInConnectionId && !profile.finden(entry.linkedInConnectionId) && (
                            <option value={entry.linkedInConnectionId}>Profil nicht mehr verbunden</option>
                          )}
                        </select>
                      </div>
                    ) : profilName(entry) && (
                      <div className="text-xs text-secondary">Profil: {profilName(entry)}</div>
                    )}
                    {entry.publishError && entry.status !== 'published' && (
                      <div className="text-xs" style={{ color: 'var(--color-danger)' }}>{entry.publishError}</div>
                    )}
                  </div>
                  <div className="calendar-list-actions">
                    <span
                      className="badge"
                      style={{
                        backgroundColor: STATUS_COLORS[entry.status] || 'grey',
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {STATUS_LABELS[entry.status] || entry.status}
                    </span>
                    {entry.status === 'draft' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStatusChange(entry.id, 'scheduled'); }}
                        className="btn btn-primary btn-sm"
                      >
                        Planen
                      </button>
                    )}
                    {entry.status === 'scheduled' && darfFreigeben && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStatusChange(entry.id, 'approved'); }}
                        className="btn btn-success btn-sm"
                      >
                        Freigeben
                      </button>
                    )}
                    {VEROEFFENTLICHBAR.includes(entry.status) && darfFreigeben && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePublishToLinkedIn(entry); }}
                          className="btn btn-success btn-sm"
                          disabled={laeuftFuer === entry.id}
                        >
                          {laeuftFuer === entry.id ? 'Wird veröffentlicht …' : 'Auf LinkedIn veröffentlichen'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setMarkieren({ entry, link: entry.linkedInPostUrl || '' }); }}
                          className="btn btn-secondary btn-sm"
                        >
                          Als veröffentlicht markieren
                        </button>
                      </>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditEntry(entry); }}
                      className="btn btn-secondary btn-sm"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteEntry(entry.id); }}
                      className="btn btn-danger btn-sm"
                    >
                      Loeschen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Instagram, X und Facebook (Liste mit Veroeffentlichen; im Kalender oben in den Tagen) */}
      <div style={viewMode === 'list' ? undefined : { display: 'none' }}>
        <SocialVeroeffentlichungen darfFreigeben={darfFreigeben} vorschauJeIdee={vorschauJeIdee} onGeladen={socialGeladen} />
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="calendar-grid-container">
          <div className="calendar-nav">
            <button onClick={() => navigateMonth(-1)} className="btn btn-secondary btn-sm">
              &lt; Vorher
            </button>
            <h3 className="calendar-month-title">{monthName}</h3>
            <button onClick={() => navigateMonth(1)} className="btn btn-secondary btn-sm">
              Nachher &gt;
            </button>
          </div>
          <div className="calendar-weekdays">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
              <div key={day} className="calendar-weekday">{day}</div>
            ))}
          </div>
          <div className="calendar-days">
            {/* Empty cells before first day */}
            {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, i) => (
              <div key={`empty-${i}`} className="calendar-day calendar-day-empty" />
            ))}
            {/* Days of month */}
            {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, i) => {
              const day = i + 1;
              const dayEntries = getEntriesForDate(day);
              const isToday =
                new Date().getDate() === day &&
                new Date().getMonth() === currentMonth.getMonth() &&
                new Date().getFullYear() === currentMonth.getFullYear();

              return (
                <div
                  key={day}
                  className={`calendar-day${isToday ? ' calendar-day-today' : ''}${dayEntries.length > 0 ? ' calendar-day-has-entries' : ''}`}
                >
                  <div className="calendar-day-number">{day}</div>
                  <div className="calendar-day-entries">
                    {dayEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="calendar-day-entry"
                        style={{ borderLeft: `3px solid ${STATUS_COLORS[entry.status] || 'grey'}` }}
                        onClick={() => setSelectedEntry(entry)}
                        title={getPostPreview(entry)}
                      >
                        <span className="calendar-entry-time">{formatTime(entry)}</span>
                        <span className="calendar-entry-preview">
                          {(entry.linkedInPost?.hook || '').substring(0, 30).replace(/\\n/g, ' ')}...
                        </span>
                      </div>
                    ))}
                    {socialFuerTag(day).map((e) => (
                      <div
                        key={e.id}
                        className="calendar-day-entry"
                        style={{ borderLeft: `3px solid ${STATUS_COLORS[e.status] || 'grey'}` }}
                        title={(e.socialPost?.text || '').substring(0, 120)}
                      >
                        <span className="calendar-entry-time">{e.scheduledTime || ''}</span>
                        <span className="calendar-entry-preview">
                          {PLATTFORM_NAME[e.platform]}: {(e.socialPost?.text || '').substring(0, 24)}...
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedEntry && !editingEntry && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Kalender-Eintrag - {formatDate(selectedEntry.scheduledDate)}
              </h2>
              <button onClick={() => setSelectedEntry(null)} className="modal-close">
                x
              </button>
            </div>

            {selectedEntry.linkedInPost?.postIdeaId && (
              <RelatedLinks
                className="mb-4"
                items={ideaBacklinks(selectedEntry.linkedInPost.postIdeaId, { postId: selectedEntry.linkedInPost.id })}
                label="Verknüpft mit dieser Idee"
              />
            )}

            <div className="info-panel info-panel-primary mb-6">
              <strong>Status:</strong>{' '}
              <span style={{ color: STATUS_COLORS[selectedEntry.status] }}>
                {STATUS_LABELS[selectedEntry.status] || selectedEntry.status}
              </span>
              <br />
              <strong>Geplant:</strong> {formatDate(selectedEntry.scheduledDate)} um {formatTime(selectedEntry)}
              {profilName(selectedEntry) && (
                <>
                  <br />
                  <strong>Profil:</strong> {profilName(selectedEntry)}
                </>
              )}
              {selectedEntry.notes && (
                <>
                  <br />
                  <strong>Notizen:</strong> {selectedEntry.notes}
                </>
              )}
              {selectedEntry.publishError && selectedEntry.status !== 'published' && (
                <>
                  <br />
                  <strong>Letzter Fehler:</strong>{' '}
                  <span style={{ color: 'var(--color-danger)' }}>{selectedEntry.publishError}</span>
                </>
              )}
              {selectedEntry.linkedInPostUrl && (
                <>
                  <br />
                  <strong>LinkedIn URL:</strong>{' '}
                  <a href={selectedEntry.linkedInPostUrl} target="_blank" rel="noopener noreferrer">
                    Link zum Beitrag
                  </a>
                </>
              )}
            </div>

            {selectedEntry.linkedInPost && (
              <div className="linkedin-preview">
                <h3 style={{ marginBottom: '12px' }}>Beitragsvorschau</h3>
                <div className="linkedin-preview-content">
                  {(selectedEntry.linkedInPost.fullPost || '').replace(/\\n/g, '\n').split('\n').map((line: string, i: number) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            <div className="btn-group mt-6">
              {selectedEntry.status === 'draft' && (
                <button
                  onClick={() => { handleStatusChange(selectedEntry.id, 'scheduled'); setSelectedEntry(null); }}
                  className="btn btn-primary"
                >
                  Planen
                </button>
              )}
              {selectedEntry.status === 'scheduled' && darfFreigeben && (
                <button
                  onClick={() => { handleStatusChange(selectedEntry.id, 'approved'); setSelectedEntry(null); }}
                  className="btn btn-success"
                >
                  Freigeben
                </button>
              )}
              {VEROEFFENTLICHBAR.includes(selectedEntry.status) && darfFreigeben && (
                <>
                  <button
                    onClick={() => { handlePublishToLinkedIn(selectedEntry); setSelectedEntry(null); }}
                    className="btn btn-success"
                  >
                    Auf LinkedIn veröffentlichen
                  </button>
                  <button
                    onClick={() => { setMarkieren({ entry: selectedEntry, link: selectedEntry.linkedInPostUrl || '' }); setSelectedEntry(null); }}
                    className="btn btn-secondary"
                  >
                    Als veröffentlicht markieren
                  </button>
                </>
              )}
              {selectedEntry.linkedInPost && (
                <button
                  onClick={() => { navigator.clipboard.writeText((selectedEntry.linkedInPost?.fullPost || '').replace(/\\n/g, '\n')); }}
                  className="btn btn-primary"
                >
                  Beitrag kopieren
                </button>
              )}
              <button
                onClick={() => { setSelectedEntry(null); handleEditEntry(selectedEntry); }}
                className="btn btn-secondary"
              >
                Bearbeiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Medium waehlen: mehrere Medien an der Beitragsidee */}
      {medienAuswahl && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Medium für LinkedIn wählen</h2>
              <button onClick={() => setMedienAuswahl(null)} className="modal-close" aria-label="Schließen">
                x
              </button>
            </div>
            <p className="text-sm text-muted">
              An der Beitragsidee hängen mehrere Medien. Welches soll mit dem Beitrag veröffentlicht werden?
            </p>
            <fieldset className="form-group" style={{ border: 'none', padding: 0 }}>
              <legend className="form-label">Medium</legend>
              {medienAuswahl.kandidaten.map((k) => (
                <label key={k.id} style={{ display: 'block', marginBottom: '6px' }}>
                  <input
                    type="radio"
                    name="linkedin-medium"
                    value={k.id}
                    checked={medienAuswahl.gewaehlt === k.id}
                    onChange={() => setMedienAuswahl({ ...medienAuswahl, gewaehlt: k.id })}
                  />{' '}
                  {k.title} ({MEDIEN_ART[k.kind] || k.kind}, {megabyte(k.sizeBytes)})
                </label>
              ))}
            </fieldset>
            <div className="btn-group mt-6">
              <button
                onClick={() => handlePublishToLinkedIn(medienAuswahl.entry, medienAuswahl.gewaehlt)}
                className="btn btn-success"
                disabled={laeuftFuer === medienAuswahl.entry.id}
              >
                Mit diesem Medium veröffentlichen
              </button>
              <button onClick={() => setMedienAuswahl(null)} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Als veroeffentlicht markieren (manuell gepostet), Link optional */}
      {markieren && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Als veröffentlicht markieren</h2>
              <button onClick={() => setMarkieren(null)} className="modal-close" aria-label="Schließen">
                x
              </button>
            </div>
            <p className="text-sm text-muted">
              Für Beiträge, die von Hand auf LinkedIn gepostet wurden. Es wird nichts auf LinkedIn veröffentlicht.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="markieren-link">Link zum LinkedIn-Beitrag (optional)</label>
              <input
                id="markieren-link"
                type="url"
                value={markieren.link}
                onChange={(e) => setMarkieren({ ...markieren, link: e.target.value })}
                className="form-input"
                placeholder="https://www.linkedin.com/feed/update/..."
              />
            </div>
            <div className="btn-group mt-6">
              <button onClick={handleMarkPublished} className="btn btn-success">
                Als veröffentlicht markieren
              </button>
              <button onClick={() => setMarkieren(null)} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Eintrag bearbeiten</h2>
              <button onClick={() => setEditingEntry(null)} className="modal-close">
                x
              </button>
            </div>
            <div className="form-group">
              <label className="form-label">Datum</label>
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Uhrzeit</label>
              <input
                type="time"
                value={editTime}
                onChange={(e) => setEditTime(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Notizen</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
                className="form-textarea"
                placeholder="Optionale Notizen..."
              />
            </div>
            <div className="btn-group mt-6">
              <button onClick={handleSaveEdit} className="btn btn-success">
                Speichern
              </button>
              <button onClick={() => setEditingEntry(null)} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicationCalendarPage;
